import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import {
  premiumDomainsData,
  authorizedPlans,
  dnsSetupGuide,
  type PremiumDomain,
  type DomainStatus,
  type DomainType,
} from '@/mocks/superAdminDomains';

const domainTypeLabel: Record<DomainType, string> = {
  rfw_subdomain: 'Subdominio RFW',
  custom_domain: 'Dominio propio',
};

const domainTypeIcon: Record<DomainType, string> = {
  rfw_subdomain: 'ri-globe-line',
  custom_domain: 'ri-earth-line',
};

const statusMeta: Record<DomainStatus, { label: string; color: string; icon: string }> = {
  active: { label: 'Activo', color: 'bg-emerald-100 text-emerald-700', icon: 'ri-check-line' },
  pending_verification: { label: 'Pendiente', color: 'bg-amber-100 text-amber-700', icon: 'ri-loader-4-line' },
  expired: { label: 'Expirado', color: 'bg-stone-100 text-stone-500', icon: 'ri-close-circle-line' },
  suspended: { label: 'Suspendido', color: 'bg-red-50 text-red-600', icon: 'ri-forbid-line' },
};

export default function SuperAdminPremiumDomains() {
  const [domains, setDomains] = useState<PremiumDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  // Modals
  const [qrTarget, setQrTarget] = useState<PremiumDomain | null>(null);
  const [dnsTarget, setDnsTarget] = useState<PremiumDomain | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(false);
    const timer = setTimeout(() => {
      if (Math.random() < 0.03) {
        setError(true);
        setLoading(false);
      } else {
        setDomains(premiumDomainsData);
        setLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const handleRetry = useCallback(() => {
    setLoading(true);
    setError(false);
    setTimeout(() => {
      if (Math.random() < 0.03) {
        setError(true);
        setLoading(false);
      } else {
        setDomains(premiumDomainsData);
        setLoading(false);
      }
    }, 500);
  }, []);

  const counts = useMemo(() => ({
    all: domains.length,
    active: domains.filter((d) => d.status === 'active').length,
    pending: domains.filter((d) => d.status === 'pending_verification').length,
    expired: domains.filter((d) => d.status === 'expired').length,
    suspended: domains.filter((d) => d.status === 'suspended').length,
  }), [domains]);

  const filteredDomains = useMemo(() => {
    let result = domains;
    if (activeFilter === 'active') result = result.filter((d) => d.status === 'active');
    if (activeFilter === 'pending') result = result.filter((d) => d.status === 'pending_verification');
    if (activeFilter === 'expired') result = result.filter((d) => d.status === 'expired');
    if (activeFilter === 'suspended') result = result.filter((d) => d.status === 'suspended');
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(
        (d) =>
          d.radioName.toLowerCase().includes(q) ||
          d.domain.toLowerCase().includes(q) ||
          d.redirectCode.toLowerCase().includes(q) ||
          d.frequency.toLowerCase().includes(q),
      );
    }
    return result;
  }, [domains, activeFilter, searchTerm]);

  const kpiCards = [
    { key: 'all',      label: 'Total',        value: counts.all,      icon: 'ri-global-line',      color: 'text-foreground-600', bg: 'bg-background-200' },
    { key: 'active',   label: 'Activos',      value: counts.active,   icon: 'ri-check-line',       color: 'text-emerald-600',    bg: 'bg-emerald-50' },
    { key: 'pending',  label: 'Pendientes',   value: counts.pending,  icon: 'ri-loader-4-line',    color: 'text-amber-600',      bg: 'bg-amber-50' },
    { key: 'suspended',label: 'Suspendidos',  value: counts.suspended,icon: 'ri-forbid-line',      color: 'text-red-500',        bg: 'bg-red-50' },
  ];

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-4 max-w-[1600px] mx-auto">

            {/* Breadcrumbs */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Dominios Premium</span>
            </div>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Dominios Premium</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  Solo planes autorizados: {authorizedPlans.join(', ')} · QR permanente vía <span className="font-mono text-foreground-500">r/codigo</span>
                </p>
              </div>
              <div className="relative w-full sm:w-64">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar radio, dominio o código..."
                  className="w-full pl-8 pr-8 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"
                  >
                    <i className="ri-close-line text-foreground-500 text-[10px]"></i>
                  </button>
                )}
              </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {kpiCards.map((card) => (
                <button
                  key={card.key}
                  onClick={() => setActiveFilter(activeFilter === card.key ? 'all' : card.key)}
                  className={`bg-background-50 border rounded-xl p-3 flex items-center gap-3 cursor-pointer transition-all hover:shadow-sm text-left ${
                    activeFilter === card.key ? 'border-foreground-300' : 'border-background-200 hover:border-background-300'
                  }`}
                >
                  <div className={`w-9 h-9 rounded-lg ${card.bg} flex items-center justify-center shrink-0`}>
                    <i className={`${card.icon} ${card.color} text-sm`}></i>
                  </div>
                  <div>
                    <p className="text-foreground-400 text-[10px] font-medium">{card.label}</p>
                    <p className="text-foreground-800 text-base font-bold">{card.value.toLocaleString()}</p>
                  </div>
                </button>
              ))}
            </div>

            {/* Filters bar */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {[
                { key: 'all',       label: 'Todos' },
                { key: 'active',    label: 'Activos' },
                { key: 'pending',   label: 'Pendientes' },
                { key: 'expired',   label: 'Expirados' },
                { key: 'suspended', label: 'Suspendidos' },
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => setActiveFilter(f.key)}
                  className={`px-3 py-1.5 rounded-full text-[10px] font-medium whitespace-nowrap cursor-pointer transition-all ${
                    activeFilter === f.key
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-50 text-foreground-500 hover:text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Table */}
            {loading ? (
              <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                <div className="w-8 h-8 mx-auto border-2 border-foreground-200 border-t-foreground-400 rounded-full animate-spin"></div>
                <p className="text-foreground-400 text-xs mt-3">Cargando dominios...</p>
              </div>
            ) : error ? (
              <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                <i className="ri-error-warning-line text-foreground-300 text-2xl"></i>
                <p className="text-foreground-400 text-xs mt-2">Error al cargar dominios</p>
                <button onClick={handleRetry} className="mt-3 px-4 py-1.5 rounded-lg bg-foreground-950 text-background-50 text-[10px] font-bold cursor-pointer whitespace-nowrap">Reintentar</button>
              </div>
            ) : (
              <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-background-200">
                        <th className="text-left px-4 py-3 text-foreground-400 text-[9px] font-bold uppercase tracking-[0.08em] whitespace-nowrap">Radio</th>
                        <th className="text-left px-4 py-3 text-foreground-400 text-[9px] font-bold uppercase tracking-[0.08em] whitespace-nowrap">Plan</th>
                        <th className="text-left px-4 py-3 text-foreground-400 text-[9px] font-bold uppercase tracking-[0.08em] whitespace-nowrap">Dominio</th>
                        <th className="text-left px-4 py-3 text-foreground-400 text-[9px] font-bold uppercase tracking-[0.08em] whitespace-nowrap">Tipo</th>
                        <th className="text-left px-4 py-3 text-foreground-400 text-[9px] font-bold uppercase tracking-[0.08em] whitespace-nowrap">Estado</th>
                        <th className="text-center px-4 py-3 text-foreground-400 text-[9px] font-bold uppercase tracking-[0.08em] whitespace-nowrap">QR Code</th>
                        <th className="text-left px-4 py-3 text-foreground-400 text-[9px] font-bold uppercase tracking-[0.08em] whitespace-nowrap">Expira</th>
                        <th className="text-right px-4 py-3 text-foreground-400 text-[9px] font-bold uppercase tracking-[0.08em] whitespace-nowrap">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDomains.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="px-4 py-16 text-center">
                            <i className="ri-search-line text-foreground-300 text-xl"></i>
                            <p className="text-foreground-400 text-xs mt-2">Sin resultados</p>
                          </td>
                        </tr>
                      ) : (
                        filteredDomains.map((d) => {
                          const isExpiring = d.status === 'active' && new Date(d.expiresAt).getTime() - Date.now() < 60 * 24 * 60 * 60 * 1000;
                          const countsDomains = d.previousDomains.length;
                          return (
                            <tr key={d.id} className="border-b border-background-100 last:border-0 hover:bg-background-50/50 transition-colors">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2.5">
                                  <div className="w-7 h-7 rounded-lg bg-background-200 flex items-center justify-center shrink-0">
                                    <i className="ri-radio-line text-foreground-500 text-xs"></i>
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-foreground-800 text-[11px] font-semibold truncate max-w-[140px]">{d.radioName}</p>
                                    <p className="text-foreground-400 text-[9px] font-mono">{d.frequency}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-[9px] font-medium whitespace-nowrap">{d.plan}</span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="min-w-0 max-w-[200px]">
                                  <p className="text-foreground-800 text-[11px] font-mono truncate">{d.domain}</p>
                                  {countsDomains > 0 && (
                                    <p className="text-foreground-300 text-[8px] mt-0.5">+{countsDomains} dominio{countsDomains !== 1 ? 's' : ''} anterior{countsDomains !== 1 ? 'es' : ''}</p>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="inline-flex items-center gap-1 text-foreground-500 text-[10px]">
                                  <i className={`${domainTypeIcon[d.domainType]} text-[9px]`}></i>
                                  {domainTypeLabel[d.domainType]}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-medium whitespace-nowrap ${statusMeta[d.status].color}`}>
                                  <i className={`${statusMeta[d.status].icon} text-[8px]`}></i>
                                  {statusMeta[d.status].label}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <button
                                  onClick={() => setQrTarget(d)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-foreground-950 text-background-50 text-[9px] font-medium hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                                >
                                  <i className="ri-qr-code-line text-[10px]"></i>
                                  Ver QR
                                </button>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-[10px] font-mono whitespace-nowrap ${isExpiring ? 'text-red-500 font-semibold' : 'text-foreground-500'}`}>
                                  {d.expiresAt}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  {d.domainType === 'custom_domain' && (
                                    <button
                                      onClick={() => setDnsTarget(d)}
                                      className="w-7 h-7 rounded-lg bg-sky-50 flex items-center justify-center cursor-pointer hover:bg-sky-100 transition-colors"
                                      title="Configuración DNS"
                                    >
                                      <i className="ri-server-line text-sky-500 text-xs"></i>
                                    </button>
                                  )}
                                  <button
                                    onClick={() => {
                                      Swal.fire({
                                        icon: 'info',
                                        title: 'QR Permanente',
                                        html: `
                                          <div style="text-align:left;font-size:12px;">
                                            <p><strong>Radio:</strong> ${d.radioName}</p>
                                            <p><strong>Código QR:</strong> <span style="font-family:monospace;">r/${d.redirectCode}</span></p>
                                            <p><strong>Apuntando a:</strong> ${d.domain}</p>
                                            <p style="margin-top:8px;color:#6b7280;">El QR es permanente. Si el dominio cambia, el QR sigue funcionando porque redirige a través del código <span style="font-family:monospace;">r/${d.redirectCode}</span>.</p>
                                          </div>
                                        `,
                                        confirmButtonColor: '#1a1a1a',
                                        customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs px-4 py-2 rounded-xl' },
                                      });
                                    }}
                                    className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                                    title="Info del QR"
                                  >
                                    <i className="ri-information-line text-foreground-400 text-xs"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Info box */}
            <div className="bg-background-50 border border-background-200 rounded-2xl p-4 md:p-5">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-accent-100 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-vip-diamond-line text-accent-600 text-sm"></i>
                </div>
                <div className="space-y-2">
                  <h4 className="text-foreground-800 text-xs font-bold">¿Cómo funciona?</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-[10px] text-foreground-500 leading-relaxed">
                    <div className="bg-background-100 rounded-xl p-3">
                      <p className="text-foreground-700 font-semibold mb-1">1. Dominio incluido</p>
                      <p>Radios en planes {authorizedPlans.join(' o ')} pueden activar un subdominio <span className="font-mono text-foreground-600">.rfw.fm</span> o conectar su propio dominio <span className="font-mono text-foreground-600">.com / .net</span>.</p>
                    </div>
                    <div className="bg-background-100 rounded-xl p-3">
                      <p className="text-foreground-700 font-semibold mb-1">2. QR permanente</p>
                      <p>Cada dominio obtiene un QR que apunta a <span className="font-mono text-foreground-600">r/codigo</span>. Luego redirige al dominio activo. El QR nunca cambia.</p>
                    </div>
                    <div className="bg-background-100 rounded-xl p-3">
                      <p className="text-foreground-700 font-semibold mb-1">3. Si cambia el dominio</p>
                      <p>El QR sigue funcionando. Solo se actualiza el destino del código de redirección. Sin reimprimir nada.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </main>
      </div>

      {/* QR Code Modal */}
      {qrTarget && (
        <>
          <div className="fixed inset-0 z-[80] bg-black/40" onClick={() => setQrTarget(null)}></div>
          <div className="fixed inset-0 z-[81] flex items-center justify-center p-4">
            <div className="bg-background-50 rounded-2xl w-full max-w-md shadow-xl">
              <div className="flex items-center justify-between p-4 border-b border-background-200">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-foreground-950 flex items-center justify-center shrink-0">
                    <i className="ri-qr-code-line text-background-50 text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-foreground-800 text-xs font-bold">QR Code · {qrTarget.radioName}</h3>
                    <p className="text-foreground-400 text-[10px] font-mono">r/{qrTarget.redirectCode}</p>
                  </div>
                </div>
                <button onClick={() => setQrTarget(null)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors">
                  <i className="ri-close-line text-foreground-500 text-sm"></i>
                </button>
              </div>
              <div className="p-6 flex flex-col items-center">
                {/* QR placeholder */}
                <div className="w-48 h-48 rounded-2xl border-2 border-foreground-950 bg-white flex items-center justify-center mb-4">
                  <div className="text-center">
                    <i className="ri-qr-code-line text-7xl text-foreground-800"></i>
                    <p className="text-foreground-400 text-[8px] font-mono mt-1">r/{qrTarget.redirectCode}</p>
                  </div>
                </div>
                <p className="text-foreground-800 text-xs font-semibold mb-1">{qrTarget.radioName}</p>
                <p className="text-foreground-500 text-[10px] font-mono mb-1">{qrTarget.frequency}</p>
                <p className="text-foreground-400 text-[10px] mb-4">
                  Redirige a: <span className="font-mono text-foreground-600">{qrTarget.domain}</span>
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  <span className="text-foreground-400 text-[9px] font-medium">QR permanente — no cambia si el dominio cambia</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* DNS Setup Modal */}
      {dnsTarget && (
        <>
          <div className="fixed inset-0 z-[80] bg-black/40" onClick={() => setDnsTarget(null)}></div>
          <div className="fixed inset-0 z-[81] flex items-center justify-center p-4">
            <div className="bg-background-50 rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b border-background-200 sticky top-0 bg-background-50">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-sky-100 flex items-center justify-center shrink-0">
                    <i className="ri-server-line text-sky-600 text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-foreground-800 text-xs font-bold">Configuración DNS</h3>
                    <p className="text-foreground-400 text-[10px] font-mono">{dnsTarget.domain}</p>
                  </div>
                </div>
                <button onClick={() => setDnsTarget(null)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors">
                  <i className="ri-close-line text-foreground-500 text-sm"></i>
                </button>
              </div>
              <div className="p-4 space-y-4">
                <p className="text-foreground-500 text-[10px]">
                  Agregá estos registros en el panel DNS de tu proveedor de dominio ({dnsTarget.domain}). Una vez configurados, la verificación es automática.
                </p>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-background-200">
                      <th className="text-left px-3 py-2 text-foreground-400 text-[9px] font-bold uppercase">Tipo</th>
                      <th className="text-left px-3 py-2 text-foreground-400 text-[9px] font-bold uppercase">Nombre</th>
                      <th className="text-left px-3 py-2 text-foreground-400 text-[9px] font-bold uppercase">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-background-100">
                      <td className="px-3 py-2 font-mono text-xs font-bold text-foreground-700">{dnsSetupGuide.aRecord.type}</td>
                      <td className="px-3 py-2 font-mono text-xs text-foreground-600">{dnsSetupGuide.aRecord.name}</td>
                      <td className="px-3 py-2 font-mono text-xs text-foreground-600">{dnsSetupGuide.aRecord.value}</td>
                    </tr>
                    <tr className="border-b border-background-100">
                      <td className="px-3 py-2 font-mono text-xs font-bold text-foreground-700">{dnsSetupGuide.cnameRecord.type}</td>
                      <td className="px-3 py-2 font-mono text-xs text-foreground-600">{dnsSetupGuide.cnameRecord.name}</td>
                      <td className="px-3 py-2 font-mono text-xs text-foreground-600">{dnsSetupGuide.cnameRecord.value}</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 font-mono text-xs font-bold text-foreground-700">{dnsSetupGuide.txtRecord.type}</td>
                      <td className="px-3 py-2 font-mono text-xs text-foreground-600">{dnsSetupGuide.txtRecord.name}</td>
                      <td className="px-3 py-2 font-mono text-[9px] text-foreground-600">{dnsSetupGuide.txtRecord.value.replace('{code}', dnsTarget.redirectCode)}</td>
                    </tr>
                  </tbody>
                </table>
                <div className={`rounded-xl p-3 flex items-center gap-2 ${dnsTarget.dnsStatus === 'ok' ? 'bg-emerald-50' : dnsTarget.dnsStatus === 'error' ? 'bg-red-50' : 'bg-amber-50'}`}>
                  <i className={`${dnsTarget.dnsStatus === 'ok' ? 'ri-check-line text-emerald-600' : dnsTarget.dnsStatus === 'error' ? 'ri-close-line text-red-500' : 'ri-loader-4-line animate-spin text-amber-600'} text-sm`}></i>
                  <span className={`text-xs font-medium ${dnsTarget.dnsStatus === 'ok' ? 'text-emerald-700' : dnsTarget.dnsStatus === 'error' ? 'text-red-600' : 'text-amber-700'}`}>
                    {dnsTarget.dnsStatus === 'ok' ? 'DNS verificado correctamente' : dnsTarget.dnsStatus === 'error' ? 'Error en configuración DNS' : 'Verificando DNS...'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">
          Super Admin · Dominios Premium
        </p>
      </div>
    </div>
  );
}